import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  AllergyChangeStatus,
  ApproveAllergyChangeData,
  RejectAllergyChangeData,
  adminAllergyChangeRequestsApi,
} from '../allergy-change-requests';

/** Exported so the sidebar's pending badge reads the same cache entry as the list page,
 *  rather than fetching the same rows again under a key of its own. */
export const adminAllergyChangeRequestsKeys = {
  all: ['admin', 'allergy-change-requests'] as const,
  list: (status?: AllergyChangeStatus) => ['admin', 'allergy-change-requests', status ?? 'all'] as const,
  detail: (id?: string) => ['admin', 'allergy-change-requests', 'detail', id ?? null] as const,
};

export function useAdminAllergyChangeRequestsListQuery(status?: AllergyChangeStatus) {
  return useQuery({
    queryKey: adminAllergyChangeRequestsKeys.list(status),
    queryFn: () => adminAllergyChangeRequestsApi.list(status),
  });
}

export function useAdminAllergyChangeRequestQuery(id?: string) {
  return useQuery({
    queryKey: adminAllergyChangeRequestsKeys.detail(id),
    queryFn: () => adminAllergyChangeRequestsApi.detail(id as string),
    enabled: Boolean(id),
  });
}

export function useApproveAllergyChangeMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ApproveAllergyChangeData }) =>
      adminAllergyChangeRequestsApi.approve(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminAllergyChangeRequestsKeys.all }),
  });
}

export function useRejectAllergyChangeMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: RejectAllergyChangeData }) =>
      adminAllergyChangeRequestsApi.reject(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminAllergyChangeRequestsKeys.all }),
  });
}
